import React from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { ChartType } from "src/utils/types";
import type { Theme } from "@mui/material/styles";
import { makeStyles, createStyles } from "@mui/styles";
import { DONUT_CHART_COLORS } from "../defaults";

export const TooltipStyles = makeStyles((theme: Theme) =>
  createStyles({
    // used to position tooltip
    tooltip: {
      marginTop: "30px",
      marginLeft: "100px",
    },
  }),
);

interface DonutChartProps {
  data: ChartType[];
  height?: number | string;
  width?: number | string;
}

const DonutChart = (props: DonutChartProps) => {
  const { data, height = "135px", width } = props;

  const labels: string[] = data.map((el: ChartType): string => el.name);
  const balances: number[] = data.map((el: ChartType): number => el.balance);
  const values: number[] = data.map((el: ChartType): number => el.value);

  // Function to calculate the percentage of a label in the donut chart.
  function calculatePercentage(label: string, data: ChartType[]) {
    // Calculate the total value
    const total = data.reduce((acc, item) => acc + item.value, 0);

    // Find the value of the specific label
    const labelValue = data.find((item) => item.name === label)?.value || 0;

    // Calculate the percentage
    const percentage = (labelValue / total) * 100;

    return percentage;
  }

  const options: ApexOptions = {
    colors: [...DONUT_CHART_COLORS],
    labels: labels,
    chart: {
      type: "donut",
      animations: { enabled: false },
      toolbar: { show: false },
      sparkline: { enabled: true },
      parentHeightOffset: 0,
    },

    // remove data from appearing on top of donut chart
    dataLabels: {
      enabled: false,
    },

    // format the tooltip
    tooltip: {
      cssClass: TooltipStyles().tooltip,
      fixed: {
        enabled: false,
      },
      y: {
        title: {
          formatter: function (seriesName: string, opts?: any): string {
            if (!opts) return "";
            // Return tooltip text
            const units: number = balances[opts.seriesIndex];
            const percent = calculatePercentage(seriesName, data).toFixed(2);
            return `${seriesName}, ${percent}%, ${units}`;
          },
        },
        formatter: function (val: number, opts?: any): string {
          // make sure this function does render unexpected strings
          return "";
        },
      },
    },
    // remove the legend
    legend: {
      show: false,
    },
    // remove any default grid padding that can offset the pie
    grid: {
      padding: { left: 0, right: 0, top: 0, bottom: 0 },
    },
    // change the hole size in the donut
    plotOptions: {
      pie: {
        expandOnClick: false,
        offsetX: 0,
        offsetY: 0,
        donut: {
          size: "50%",
        },
      },
    },
  };

  return (
    <div style={{ display: "inline-block" }}>
      <Chart
        series={values}
        type="donut"
        options={options}
        height={height}
        width={width ?? height}
      />
    </div>
  );
};

export default DonutChart;
